import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, PRO_MEMBERSHIP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {Card, Skeleton} from '@mui/material';
import {useState, useEffect} from 'react';
import {
  getUbaPageName,
  TrackEventUBA,
} from '@jobseeker/common-core/utils/analytics';
import {getKeyFromPath, getUbaSource} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {
  MembershipHeroSection,
  MembershipSlider,
  MembershipFeaturesSection,
} from '@jobseeker/feature-payment/components/Membership';
import 'swiper/css';
import 'swiper/css/navigation';
import type {GetServerSidePropsContext} from 'next/types';
import {InjectSearchOverlay} from '@components';
import {AppState} from '@jobseeker/common-core/types';
import {set_user_info} from '@jobseeker/common-core/reducers/auth';
import {gladiatorService} from '@jobseeker/common-core/services';
import cookie from 'cookie';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';

type ProMembershipProps = {
  seoConfig: object;
  queryParams: {
    recappid?: string;
    refcode?: string;
    pageid?: string;
    reffeature?: string;
  };
};

const ProMembership = ({seoConfig, queryParams}: ProMembershipProps) => {
  const metaData = getConfigByApp(metaInfo, HOME, true, PRO_MEMBERSHIP);
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  const router = useRouter();

  useValidateAuth(dispatch);

  const {searchOverlay: isSearchEnabled} = useSelector(
    (state: AppState) => state?.app?.layout,
    shallowEqual
  );

  const profile = useSelector((state: AppState) => state.user.profile);
  const profileLoading = useSelector(
    (state: AppState) => state.user.profileLoading
  );

  const showRecruiterActions =
    profile?.proMembershipFeatures?.statusChange || false;

  const showLoader = !mounted || profileLoading || profileLoading === null;

  useEffect(() => {
    if (profileLoading === false) {
      const ubaSource = getUbaSource({
        pageid: queryParams?.pageid || router?.query?.pageid || '',
        reffeature: queryParams?.reffeature || router?.query?.reffeature || '',
        source: router?.query?.source || 'Profile',
      });

      TrackEventUBA('proMembershipPageView', 'view', {
        ref: getKeyFromPath('ref') || '',
        source: ubaSource,
        page: getUbaPageName(),
      });
    }
  }, [
    profileLoading,
    showRecruiterActions,
    profile?.proMembership?.isValid,
    queryParams?.pageid,
    router.query?.pageid,
    router.query?.source,
  ]);

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Pro-membership',
      item: ``,
    },
  ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
          <Card
            sx={{
              maxWidth: 1050,
              margin: '0 auto',
              borderRadius: '16px',
            }}
          >
            {showLoader ? (
              <>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={400}
                  sx={{mb: 2, borderRadius: '16px 16px 0 0'}}
                />
                <Skeleton variant="rectangular" width="100%" height={200} />
              </>
            ) : (
              <>
                {!showRecruiterActions && (
                  <MembershipHeroSection queryParams={queryParams} />
                )}
                {!showRecruiterActions && <MembershipSlider />}
              </>
            )}
            {!showLoader && (
              <MembershipFeaturesSection queryParams={queryParams} />
            )}
          </Card>
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const recappid = context.query?.recappid || null;
    const refcode = context.query?.refcode || null;
    const pageid = context.query?.pageid || null;

    const queryParams = {recappid, refcode, pageid};
    const seoConfig = await loadSEO(PRO_MEMBERSHIP);

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const authInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          if (authInfo?.userId) {
            store.dispatch(
              set_user_info({
                userId: authInfo.userId,
                obfuscatedUserId: authInfo.obfuscatedUserId || '',
              })
            );
          }
        }
      }
    } catch (e) {}

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: PRO_MEMBERSHIP},
    });
    return {
      props: {
        seoConfig,
        queryParams,
      },
    };
  }
);

export default ProMembership;
